// src/components/layout/layoutConfig.ts
// Layout-wide toggles. Edit values here and every BaseLayout-based page picks up the change.
import { useEffect, useState } from 'react';
import CONFIG from '@/lib/config';

/**
 * Layout shell style — TEST TOGGLE (cycles through 3 variants).
 *   - 'classic'        → previous flush layout (edge-to-edge sidebar + content).
 *   - 'sidebar-island' → only the sidebar floats as a card; content stays flush.
 *   - 'island'         → sidebar AND content are floating rounded cards with gaps.
 *
 * Persisted in localStorage so you can flip it live (see the "Layout" item in
 * the user menu) without a rebuild. To retire the experiment later: pick the
 * winner, hardcode it, and delete the toggle (this block + the UserHeader item).
 */
export type LayoutMode = 'classic' | 'sidebar-island' | 'island';
const LAYOUT_MODE_KEY = 'nexilink-layout-mode';
const LAYOUT_MODE_EVENT = 'nexilink-layout-mode-change';
export const DEFAULT_LAYOUT_MODE: LayoutMode = 'island';

// Cycle order for the toggle + Swedish labels for the menu item.
export const LAYOUT_MODE_ORDER: LayoutMode[] = ['classic', 'sidebar-island', 'island'];
export const LAYOUT_MODE_LABELS: Record<LayoutMode, string> = {
    classic: 'Klassisk',
    'sidebar-island': 'Sidofält-ö',
    island: 'Ö-läge',
};

export function nextLayoutMode(mode: LayoutMode): LayoutMode {
    const i = LAYOUT_MODE_ORDER.indexOf(mode);
    return LAYOUT_MODE_ORDER[(i + 1) % LAYOUT_MODE_ORDER.length];
}

export function getLayoutMode(): LayoutMode {
    if (typeof window === 'undefined') return DEFAULT_LAYOUT_MODE;
    // The layout switcher is DEV-only (#37). Outside dev-mode everyone gets the
    // pinned DEFAULT_LAYOUT_MODE (Ö-läge) — a stale persisted mode can't strand a
    // user on a variant they have no toggle to change.
    if (!CONFIG.SHOW_DEV_TOOLS) return DEFAULT_LAYOUT_MODE;
    const v = window.localStorage.getItem(LAYOUT_MODE_KEY) as LayoutMode | null;
    return v && LAYOUT_MODE_ORDER.includes(v) ? v : DEFAULT_LAYOUT_MODE;
}

export function setLayoutMode(mode: LayoutMode): void {
    window.localStorage.setItem(LAYOUT_MODE_KEY, mode);
    window.dispatchEvent(new Event(LAYOUT_MODE_EVENT));
}

/** Reactive hook — re-renders on toggle (this tab via custom event, other tabs via storage). */
export function useLayoutMode(): LayoutMode {
    const [mode, setMode] = useState<LayoutMode>(getLayoutMode);
    useEffect(() => {
        const handler = () => setMode(getLayoutMode());
        window.addEventListener(LAYOUT_MODE_EVENT, handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener(LAYOUT_MODE_EVENT, handler);
            window.removeEventListener('storage', handler);
        };
    }, []);
    return mode;
}

/**
 * Max width applied to scrollable dashboard pages (BaseLayout `scrollable` variant).
 *   - `null`        → full width, content fills the viewport (legacy behavior).
 *   - `<className>` → any Tailwind max-width class, e.g. `'max-w-screen-2xl'` (1536px).
 *
 * `max-w-screen-2xl` is a sensible default: fits common laptop/monitor widths without
 * feeling cramped, and prevents content from sprawling across ultrawide displays.
 *
 * Note: the `fullHeight` variant (chat/map/kanban-style pages) intentionally ignores
 * this — those pages are edge-to-edge by design.
 */
export const DASHBOARD_MAX_WIDTH: string | null = 'max-w-screen-2xl';
